import { useMemo, useState } from "react";
import { toast } from "sonner";
import Organisation from "@/model/interface/Organisation";
import {
  useCreateComplimentaryAgentMutation,
  useGetAiTemplatesQuery,
  useGetDeployedAgentsQuery,
} from "@/store/admin_api";
import { AiAgent, AiAgentTemplate, getLlmModelsForProvider } from "@/model/interface/AiAgent";
import { apiErrorMessage } from "@/util/apiError";

export const ASSIGN_AGENT_INHERIT = "__inherit__";

export interface AssignAgentFormState {
  pickedOrganisation: Organisation | null;
  sourceKind: "template" | "agent";
  templateId: string;
  sourceAgentId: string;
  name: string;
  greeting: string;
  orchestrator: string;
  voiceProvider: string;
  llmProvider: string;
  llmModel: string;
  direction: string;
}

export interface AssignAgentFormController {
  state: AssignAgentFormState;
  setField: <Field extends keyof AssignAgentFormState>(field: Field, value: AssignAgentFormState[Field]) => void;
  reset: () => void;
  organisation: Organisation | null;
  fixedOrganisation?: Organisation | null;
  templates: AiAgentTemplate[];
  deployedAgents: AiAgent[];
  selectedTemplate?: AiAgentTemplate;
  selectedSourceAgent?: AiAgent;
  sourceName?: string;
  sourceGreeting?: string;
  modelOptions: ReturnType<typeof getLlmModelsForProvider>;
  hasSource: boolean;
  canSubmit: boolean;
  isCreating: boolean;
  createAgent: () => Promise<AiAgent | null>;
}

export default function useAssignAgentForm({
  fixedOrganisation,
  initialTemplateId,
  initialSourceAgentId,
}: {
  fixedOrganisation?: Organisation | null;
  initialTemplateId?: string;
  initialSourceAgentId?: string;
} = {}): AssignAgentFormController {
  const [createComplimentaryAgent, { isLoading: isCreating }] = useCreateComplimentaryAgentMutation();
  const { data: templatePage } = useGetAiTemplatesQuery({ size: 200 });
  const { data: deployedPage } = useGetDeployedAgentsQuery({ page: 0, size: 50 });

  const initialState: AssignAgentFormState = {
    pickedOrganisation: null,
    sourceKind: initialSourceAgentId ? "agent" : "template",
    templateId: initialTemplateId ?? "",
    sourceAgentId: initialSourceAgentId ?? "",
    name: "",
    greeting: "",
    orchestrator: ASSIGN_AGENT_INHERIT,
    voiceProvider: ASSIGN_AGENT_INHERIT,
    llmProvider: ASSIGN_AGENT_INHERIT,
    llmModel: ASSIGN_AGENT_INHERIT,
    direction: ASSIGN_AGENT_INHERIT,
  };

  const [state, setState] = useState<AssignAgentFormState>(initialState);

  const templates = templatePage?.items ?? [];
  const deployedAgents = deployedPage?.items ?? [];
  const organisation = fixedOrganisation ?? state.pickedOrganisation;

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === state.templateId),
    [templates, state.templateId],
  );
  const selectedSourceAgent = useMemo(
    () => deployedAgents.find((agent) => agent.id === state.sourceAgentId),
    [deployedAgents, state.sourceAgentId],
  );

  const sourceName = state.sourceKind === "template" ? selectedTemplate?.name : selectedSourceAgent?.name;
  const sourceGreeting =
    state.sourceKind === "template"
      ? selectedTemplate?.defaultGreeting
      : selectedSourceAgent?.greeting ?? selectedSourceAgent?.template?.defaultGreeting;

  const modelOptions = getLlmModelsForProvider(
    state.llmProvider === ASSIGN_AGENT_INHERIT ? null : state.llmProvider,
  );

  const hasSource = state.sourceKind === "template" ? Boolean(state.templateId) : Boolean(state.sourceAgentId);
  const canSubmit = Boolean(organisation) && hasSource && !isCreating;

  const setField = <Field extends keyof AssignAgentFormState>(field: Field, value: AssignAgentFormState[Field]) => {
    setState((previous) => {
      if (field === "sourceKind") {
        return { ...previous, sourceKind: value as AssignAgentFormState["sourceKind"], templateId: "", sourceAgentId: "" };
      }
      if (field === "llmProvider") {
        return { ...previous, llmProvider: value as string, llmModel: ASSIGN_AGENT_INHERIT };
      }
      return { ...previous, [field]: value };
    });
  };

  const reset = () => {
    setState(initialState);
  };

  const createAgent = async (): Promise<AiAgent | null> => {
    if (!organisation || !hasSource) {
      return null;
    }
    try {
      const createdAgent = await createComplimentaryAgent({
        organisationId: organisation.id,
        templateId: state.sourceKind === "template" ? state.templateId : undefined,
        sourceAgentId: state.sourceKind === "agent" ? state.sourceAgentId : undefined,
        name: state.name.trim() || undefined,
        greeting: state.greeting.trim() || undefined,
        orchestrator: state.orchestrator === ASSIGN_AGENT_INHERIT ? undefined : state.orchestrator,
        voiceProvider: state.voiceProvider === ASSIGN_AGENT_INHERIT ? undefined : state.voiceProvider,
        llmProvider: state.llmProvider === ASSIGN_AGENT_INHERIT ? undefined : state.llmProvider,
        llmModel: state.llmModel === ASSIGN_AGENT_INHERIT ? undefined : state.llmModel,
        direction: state.direction === ASSIGN_AGENT_INHERIT ? undefined : state.direction,
      }).unwrap();
      toast.success(`Complimentary agent assigned to ${organisation.name}`);
      return createdAgent;
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to assign agent"));
      return null;
    }
  };

  return {
    state,
    setField,
    reset,
    organisation,
    fixedOrganisation,
    templates,
    deployedAgents,
    selectedTemplate,
    selectedSourceAgent,
    sourceName,
    sourceGreeting,
    modelOptions,
    hasSource,
    canSubmit,
    isCreating,
    createAgent,
  };
}
